import React from 'react';
import EditableCell from 'components/common/EditableCell';

export interface TextAreaFieldProps {
  /** Label shown to the left of the textarea. Omit when an external heading
   *  already labels the field (e.g. a section heading above the textarea). */
  label?: string;
  value: string | null;
  /** Placeholder text shown grayed out when value is null, and as the
   *  textarea's own placeholder while editing. */
  placeholder?: string;
  /** When set, enables the character-count display.
   *  Hard-blocked at this limit by default; set softLimit to warn instead. */
  maxLength?: number;
  /** Extra className forwarded to the EditableCell (controls width, bg, etc.). */
  inputClassName?: string;
  disabled?: boolean;
  /**
   * Always show the textarea without requiring a click to enter edit mode.
   * Use in form/modal contexts where the field should always be editable.
   * Default: false (click-to-edit).
   */
  alwaysEdit?: boolean;
  /**
   * Fires on every keystroke in alwaysEdit mode for live parent-state updates.
   * Ignored in click-to-edit mode.
   */
  onChange?: (value: string) => void;
  /** Fixed row count in alwaysEdit mode. Omit to use autoSize. */
  rows?: number;
  /** Text appended after the count, e.g. ' characters' → '42/250 characters'. */
  counterSuffix?: string;
  /** Counter position: 'right' (default, mirrors AntD showCount) or 'left'. */
  counterPosition?: 'left' | 'right';
  /** Override className for the counter text element. */
  counterClassName?: string;
  /**
   * false (default): input hard-blocked at maxLength.
   * true: user can type past maxLength; counter turns danger-coloured.
   */
  softLimit?: boolean;
  onCommit: (value: string | null) => void;
}

/**
 * Inline row: label — multi-line text EditableCell with optional character count.
 *
 * Covers the repeated pattern of a labeled, optionally-bounded textarea field
 * (e.g. Notes). The label aligns to the top of the textarea (`items-start` +
 * `pt-1.5`) rather than the centre, matching AntD TextArea's own top-anchoring.
 */
const TextAreaField: React.FC<TextAreaFieldProps> = ({
  label,
  value,
  placeholder,
  maxLength,
  inputClassName,
  disabled,
  alwaysEdit,
  onChange,
  rows,
  counterSuffix,
  counterPosition,
  counterClassName,
  softLimit,
  onCommit,
}) => {
  const displayValue =
    !value && placeholder ? (
      <span className="text-gray-muted">{placeholder}</span>
    ) : (
      value
    );

  return (
    <div className="flex items-start gap-2">
      {label && (
        <span className="w-20 flex-shrink-0 whitespace-nowrap pt-1.5 text-sm font-semibold text-gray-dark">
          {label}
        </span>
      )}
      <EditableCell
        type="text"
        value={value}
        displayValue={displayValue}
        placeholder={placeholder}
        maxLength={maxLength}
        disabled={disabled}
        className={inputClassName}
        alwaysEdit={alwaysEdit}
        onChange={onChange}
        rows={rows}
        counterSuffix={counterSuffix}
        counterPosition={counterPosition}
        counterClassName={counterClassName}
        softLimit={softLimit}
        onCommit={onCommit}
      />
    </div>
  );
};

export default TextAreaField;
